import { Button } from "@resource/atlas/button/Button";
import { Icon } from "@resource/atlas/icon/Icon";
import {
  atlasCaretDown,
  atlasChevronLeft,
  atlasChevronRight,
} from "@resource/atlas/icons";
import * as Select from "@resource/atlas/select-v2/Select";
import { useSelectItems as useSelectItemsV2 } from "@resource/atlas/select-v2/use-select-items";
import Tooltip from "@resource/atlas/tooltip/Tooltip";
import clsx from "clsx";

const RIGHT_ICON = atlasChevronRight;
const LEFT_ICON = atlasChevronLeft;

export interface PaginationProps {
  totalCount: number;
  pageSize: number;
  pageIndex: number;
  isLoading: boolean;
  onPrevPage: () => void;
  onNextPage: () => void;
  canNextPage: boolean;
  canPreviousPage: boolean;
  onPageSizeChange: (pageSize: number) => void;
  className?: string;
}

export function Pagination({
  totalCount,
  pageSize,
  pageIndex,
  isLoading,
  onPrevPage,
  onNextPage,
  canNextPage,
  canPreviousPage,
  onPageSizeChange,
  className,
}: PaginationProps) {
  const prevPageTooltip = "Previous page";
  const nextPageTooltip = "Next page";

  const lowerLimit = Math.max(pageSize * pageIndex + 1, 1);
  const upperLimit = Math.min(pageSize * (pageIndex + 1), totalCount);

  const menuItems = useSelectItemsV2(
    (i) => [
      i.option({
        children: "10 per page",
        key: "10",
        value: "10",
        onClick: () => onPageSizeChange(10),
      }),
      i.option({
        children: "25 per page",
        key: "25",
        value: "25",
        onClick: () => onPageSizeChange(25),
      }),
      i.option({
        children: "50 per page",
        key: "50",
        value: "50",
        onClick: () => onPageSizeChange(50),
      }),
      i.option({
        children: "100 per page",
        key: "100",
        value: "100",
        onClick: () => onPageSizeChange(100),
      }),
    ],
    [onPageSizeChange]
  );

  return (
    <div className={clsx("flex justify-between items-center", className)}>
      <div className="text-body-md">
        {lowerLimit} - {upperLimit} of {totalCount}
      </div>
      <div className="flex items-center">
        <Select.Root value={pageSize.toString()}>
          <Select.Trigger size="small" className="bg-transparent text-body-md">
            {pageSize} per page <Icon content={atlasCaretDown} />
          </Select.Trigger>
          <Select.Content items={menuItems} />
        </Select.Root>
        <Tooltip content={prevPageTooltip}>
          <Button
            className="ml-4"
            aria-label={prevPageTooltip}
            disabled={!canPreviousPage || isLoading}
            isGhost
            icon={LEFT_ICON}
            onClick={onPrevPage}
          />
        </Tooltip>
        <Tooltip content={nextPageTooltip}>
          <Button
            aria-label={nextPageTooltip}
            disabled={!canNextPage || isLoading}
            isGhost
            icon={RIGHT_ICON}
            onClick={onNextPage}
          />
        </Tooltip>
      </div>
    </div>
  );
}
